import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  backendService,
  type MarketplaceBid,
  type MarketplaceJob,
  type MarketplaceQuote,
  type MarketplaceSliceEstimate,
  type PrintProfile
} from '../../../services/backendService'
import { MarketplaceStlPreview } from './MarketplaceStlPreview'
import '../styles/JobDetailsForFarm.css'

interface JobDetailsForFarmProps {
  job: MarketplaceJob
  onBack: () => void
  onSubmitBid: () => void
  onUpdateJob: (job: MarketplaceJob) => void
  isVerified?: boolean
  alreadyBid?: boolean
  alreadyQuoted?: boolean
  pendingBid?: MarketplaceBid
  pendingQuote?: MarketplaceQuote
  isWithdrawingOffer?: boolean
  onCancelPendingBid?: (bid: MarketplaceBid) => void
  onCancelPendingQuote?: (quote: MarketplaceQuote) => void
}

export const JobDetailsForFarm: React.FC<JobDetailsForFarmProps> = ({
  job,
  onBack,
  onSubmitBid,
  onUpdateJob,
  isVerified = true,
  alreadyBid = false,
  alreadyQuoted = false,
  pendingBid,
  pendingQuote,
  isWithdrawingOffer = false,
  onCancelPendingBid,
  onCancelPendingQuote
}) => {
  const MARKETPLACE_FEE_RATE = 0.1
  const [profiles, setProfiles] = useState<PrintProfile[]>([])
  const [selectedProfileId, setSelectedProfileId] = useState('')
  const [estimate, setEstimate] = useState<MarketplaceSliceEstimate | null>(job.estimate || null)
  const [estimateError, setEstimateError] = useState<string | null>(null)
  const [isEstimating, setIsEstimating] = useState(false)
  const [isLoadingPackage, setIsLoadingPackage] = useState(false)
  const [showSlicerStudio, setShowSlicerStudio] = useState(false)
  const [profileOverrides, setProfileOverrides] = useState({
    layerHeightMm: '',
    infillDensity: '',
    printSpeedMmS: '',
    supportEnabled: false
  })

  useEffect(() => {
    const loadContext = async () => {
      setIsLoadingPackage(true)
      setEstimateError(null)
      try {
        const [pkg, farmProfiles] = await Promise.all([
          backendService.getMarketplaceJobManufacturingPackage(job.id),
          backendService.listPrintProfiles()
        ])
        setProfiles(farmProfiles)
        setEstimate(pkg.job.estimate || null)
        onUpdateJob({ ...job, ...pkg.job })
      } catch (error) {
        setEstimateError(error instanceof Error ? error.message : 'Failed to load the manufacturing package')
      } finally {
        setIsLoadingPackage(false)
      }
    }

    void loadContext()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id])

  const selectedProfile = useMemo(
    () => profiles.find(profile => profile.id === selectedProfileId) || null,
    [profiles, selectedProfileId]
  )
  const selectedPrice = useMemo(() => {
    if (typeof job.fixedPrice === 'number') {
      return job.fixedPrice
    }

    if (typeof estimate?.totalCost === 'number') {
      return estimate.totalCost
    }

    return null
  }, [estimate?.totalCost, job.fixedPrice])
  const marketplaceFeeAmount = selectedPrice ? selectedPrice * MARKETPLACE_FEE_RATE : null
  const estimatedFarmPayout = selectedPrice && marketplaceFeeAmount !== null
    ? selectedPrice - marketplaceFeeAmount
    : null
  const loadSharedStl = useCallback(() => backendService.downloadMarketplaceJobSTL(job.id), [job.id])

  const buildOverridesPayload = () => ({
    ...(profileOverrides.layerHeightMm ? { layer_height_mm: parseFloat(profileOverrides.layerHeightMm) } : {}),
    ...(profileOverrides.infillDensity ? { infill_density: parseFloat(profileOverrides.infillDensity) } : {}),
    ...(profileOverrides.printSpeedMmS ? { print_speed_mm_s: parseFloat(profileOverrides.printSpeedMmS) } : {}),
    support_enabled: profileOverrides.supportEnabled
  })

  const handleEstimate = async () => {
    setIsEstimating(true)
    setEstimateError(null)
    try {
      const result = await backendService.estimateMarketplaceJobForFarm(job.id, {
        profileId: selectedProfileId || undefined,
        profileOverrides: buildOverridesPayload()
      })
      setEstimate(result.estimate)
      onUpdateJob({
        ...job,
        estimate: result.estimate,
        printProfileSnapshot: result.profile,
        profileSummary: {
          id: selectedProfileId || job.profileSummary?.id,
          name: (result.profile?.name as string | undefined) || selectedProfile?.name || job.profileSummary?.name || null,
          printerModel: (result.profile?.printer_model as string | undefined) || selectedProfile?.printerModel || job.profileSummary?.printerModel || null,
          material: (result.profile?.material as string | undefined) || selectedProfile?.material || job.profileSummary?.material || null
        }
      })
    } catch (error) {
      setEstimateError(error instanceof Error ? error.message : 'Failed to re-estimate job')
    } finally {
      setIsEstimating(false)
    }
  }

  return (
    <div className="job-details-farm">
      <button className="btn-back" onClick={onBack}>
        ← Back to Jobs
      </button>

      <div className="job-details-farm-header">
        <div className="job-info">
          <h2>{job.title}</h2>
          <p className="description">{job.description}</p>

          <div className="specs-grid">
            <div className="spec-item">
              <span className="label">Material</span>
              <span className="value">{job.material}</span>
            </div>
            <div className="spec-item">
              <span className="label">Deadline</span>
              <span className="value">{job.deadline}</span>
            </div>
            <div className="spec-item">
              <span className="label">Pricing Model</span>
              <span className="value">
                {job.pricingModel === 'bid' ? 'Bid System' : 'Fixed Price'}
              </span>
            </div>
          </div>

          {selectedPrice !== null && (
            <div className="farm-fee-summary">
              <h3>Final Price Summary</h3>
              <div><strong>Order Price:</strong> ${selectedPrice.toFixed(2)}</div>
              <div><strong>Marketplace Fee ({(MARKETPLACE_FEE_RATE * 100).toFixed(0)}%):</strong> ${marketplaceFeeAmount?.toFixed(2)}</div>
              <div><strong>Estimated Payout to Farm:</strong> ${estimatedFarmPayout?.toFixed(2)}</div>
            </div>
          )}

          <label className="farm-slicer-toggle" data-testid="farm-slicer-studio-toggle">
            <input
              type="checkbox"
              checked={showSlicerStudio}
              onChange={event => setShowSlicerStudio(event.target.checked)}
            />
            <span>Show Slicer Studio (preset selection + overrides + farm-side recalculation)</span>
          </label>

          {showSlicerStudio && (
          <div className="farm-slicing-panel" data-testid="farm-slicer-studio-panel">
            <h3>Shared Manufacturing Package</h3>
            <p className="farm-slicing-copy">Use the doctor&apos;s shared preset as a baseline, or switch to one of your own farm presets and recalculate a bid-ready estimate.</p>

            <div className="specs-grid compact">
              <div className="spec-item">
                <span className="label">Doctor Preset</span>
                <span className="value">{job.profileSummary?.name || 'Default estimate profile'}</span>
              </div>
              <div className="spec-item">
                <span className="label">Printer</span>
                <span className="value">{job.profileSummary?.printerModel || 'Generic FDM Printer'}</span>
              </div>
            </div>

            <div className="farm-estimate-controls">
              <div className="form-group-inline">
                <label>My Farm Profile</label>
                <select value={selectedProfileId} onChange={event => setSelectedProfileId(event.target.value)}>
                  <option value="">Use doctor&apos;s shared profile snapshot</option>
                  {profiles.map(profile => (
                    <option key={profile.id} value={profile.id}>{profile.name} · {profile.printerModel || 'Generic'} · {profile.material}</option>
                  ))}
                </select>
              </div>

              <div className="form-group-inline">
                <label>Layer Height (mm)</label>
                <input type="number" step="0.01" min="0.05" value={profileOverrides.layerHeightMm} onChange={event => setProfileOverrides(prev => ({ ...prev, layerHeightMm: event.target.value }))} placeholder="Use preset" />
              </div>

              <div className="form-group-inline">
                <label>Infill</label>
                <input type="number" step="0.01" min="0.01" max="1" value={profileOverrides.infillDensity} onChange={event => setProfileOverrides(prev => ({ ...prev, infillDensity: event.target.value }))} placeholder="0.18" />
              </div>

              <div className="form-group-inline">
                <label>Print Speed (mm/s)</label>
                <input type="number" step="1" min="5" value={profileOverrides.printSpeedMmS} onChange={event => setProfileOverrides(prev => ({ ...prev, printSpeedMmS: event.target.value }))} placeholder="Use preset" />
              </div>
            </div>

            <label className="checkbox-inline">
              <input type="checkbox" checked={profileOverrides.supportEnabled} onChange={event => setProfileOverrides(prev => ({ ...prev, supportEnabled: event.target.checked }))} />
              Enable support material in the farm-side recalculation
            </label>

            <div className="estimate-bar">
              <button className="btn-secondary" type="button" onClick={handleEstimate} disabled={isEstimating || isLoadingPackage}>
                {isEstimating ? 'Recalculating...' : 'Recalculate Farm Estimate'}
              </button>
            </div>

            {estimate && (
              <div className="estimate-summary-card">
                <div><strong>Material:</strong> {estimate.materialGrams?.toFixed(2)} g</div>
                <div><strong>Time:</strong> {estimate.printTimeMinutes} min</div>
                <div><strong>Material Cost:</strong> ${estimate.materialCost?.toFixed(2)}</div>
                <div><strong>Total Cost Floor:</strong> ${estimate.totalCost?.toFixed(2)}</div>
                {estimate.warnings?.length ? (
                  <ul>
                    {estimate.warnings.map(warning => <li key={warning}>{warning}</li>)}
                  </ul>
                ) : null}
              </div>
            )}

            {estimateError && <div className="estimate-error">{estimateError}</div>}
          </div>
          )}

          {job.pricingModel === 'fixed_price' && job.fixedPrice && (

            <div className="fixed-price-info">
              <h3>Fixed Price: ${job.fixedPrice.toFixed(2)}</h3>
            </div>
          )}
        </div>

        <div className="doctor-info">
          <div className="doctor-card">
            <h4>Doctor Information</h4>
            <p className="doctor-name">{job.doctorName}</p>
            <div className="doctor-rating">
              <span className="stars">⭐ {job.doctorRating.toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="stl-preview">
        <MarketplaceStlPreview loadStl={loadSharedStl} fileName={job.stlFileName} />
      </div>

      <div className="job-actions">
        {alreadyQuoted ? (
          <div className="verification-warning" role="status">
            <span>✓ You already submitted a quote for this fixed-price job. Awaiting doctor decision.</span>
            {pendingQuote && onCancelPendingQuote && (
              <button
                type="button"
                className="btn-secondary"
                data-testid="cancel-pending-quote-btn"
                onClick={() => onCancelPendingQuote(pendingQuote)}
                disabled={isWithdrawingOffer}
              >
                {isWithdrawingOffer ? 'Cancelling quote...' : 'Cancel Quote'}
              </button>
            )}
          </div>
        ) : alreadyBid ? (
          <div className="verification-warning" role="status">
            <span>✓ You already submitted a bid for this job. Track its status in My Bids.</span>
            {pendingBid && onCancelPendingBid && (
              <button
                type="button"
                className="btn-secondary"
                data-testid="cancel-pending-bid-btn"
                onClick={() => onCancelPendingBid(pendingBid)}
                disabled={isWithdrawingOffer}
              >
                {isWithdrawingOffer ? 'Cancelling bid...' : 'Cancel Bid'}
              </button>
            )}
          </div>
        ) : (
          <button className="btn-primary" onClick={onSubmitBid} disabled={!isVerified}>
            {job.pricingModel === 'fixed_price' ? '✓ Accept Job' : '📝 Submit Bid'}
          </button>
        )}
        {!isVerified && (
          <p className="verification-warning">Your farm must be verified by an admin before you can submit bids or accept jobs.</p>
        )}
      </div>
    </div>
  )
}

